import { FormHelperText, LinearProgress, Stack, TextField } from "@mui/material";
import { omitBy } from "lodash-es";
import { useCallback, useEffect, useRef, type FC } from "react";
import { EmailEditor as UnlayerEmailEditor } from "react-email-editor";
import { config } from "../../../../config";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import type { UUID } from "../../../../utils/uuid";
import { EmailAddressSelect } from "../../../email-moment/components/email-address-selector";
import { EmailMomentLoader } from "../../../email-moment/server/email-moment-loader";
import type { EmailMoment } from "../../../email-moment/types";
import { AutoCompleteSelector } from "../../../generic/components/auto-complete/auto-complete";
import { SimpleSaver, type SimpleSavable } from "../../../generic/components/simple-saver";
import { useFilterTags } from "../../../merge-tag/components/use-filter-tags";
import type { Tags } from "../../../merge-tag/types";
import { mapUnlayerMergeTags } from "../../../merge-tag/util";
import type { ToTarget } from "../../../to/use-to-target";
import { DisplayConditionDialog } from "../../../unlayer-email-content/components/display-condition-dialog";
import { useUnlayerEditorOptions } from "../../../unlayer-email-content/hooks/use-unlayer-editor-options";
import type { UnlayerDesign } from "../../../unlayer-email-content/types/unlayer-design";
import { useFollowUpDisplayCondition } from "../../hooks/use-follow-up-display-condition";
import { useFollowUpUnlayerEditor } from "../../hooks/use-follow-up-unlayer-editor";
import type { FollowUpChannelWithData } from "../../reducer/schema";

interface FollowUpMessageEmailNewsletterProps {
  channel: Extract<FollowUpChannelWithData, { channel: "work_email" | "personal_email" }>;
  setChannelData: (data: FollowUpChannelWithData) => void;
  mergeTags: Tags;
  toTarget: ToTarget;
  emailMoment: EmailMoment | null;
  onEmailMomentChange: (emailMoment: EmailMoment) => void;
  restrictedEmailFrom: UUID[];
  dynamicSegmentIdMap: Record<string, string> | null;
  onDynamicSegmentIdMapChange: (map: Record<string, string> | null) => void;
  errors: {
    email_sender?: string;
    email_subject?: string;
    design?: string;
  };
  disabled: boolean;
}

export const FollowUpMessageEmailNewsletter: FC<FollowUpMessageEmailNewsletterProps> = ({
  channel,
  setChannelData,
  mergeTags,
  toTarget,
  emailMoment,
  onEmailMomentChange,
  restrictedEmailFrom,
  dynamicSegmentIdMap,
  onDynamicSegmentIdMapChange,
  errors,
  disabled,
}) => {
  const saverRef = useRef<SimpleSavable>(null);
  const emailMomentRef = useRef(emailMoment);
  emailMomentRef.current = emailMoment;

  const loadNewEmailMoment = useSupabaseCallback(
    async ({ supabase }) => {
      const newEmailMoment = await new EmailMomentLoader(supabase).loadNew();
      onEmailMomentChange(newEmailMoment);
    },
    [onEmailMomentChange],
  );

  useEffect(() => {
    if (!emailMoment) {
      void loadNewEmailMoment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on mount
  }, []);

  const dynamicSegmentIdMapRef = useRef(dynamicSegmentIdMap);
  dynamicSegmentIdMapRef.current = dynamicSegmentIdMap;

  const displayCondition = useFollowUpDisplayCondition({
    getDynamicSegmentIdMap: useCallback(() => dynamicSegmentIdMapRef.current, []),
    setDynamicSegmentIdMap: useCallback(
      (map: Record<string, string>) => onDynamicSegmentIdMapChange(map),
      [onDynamicSegmentIdMapChange],
    ),
    getDesign: useCallback(() => emailMomentRef.current?.design ?? null, []),
  });

  const handleDesignChange = useCallback(
    (design: UnlayerDesign, html: string) => {
      if (!emailMoment) return;
      onEmailMomentChange({
        ...emailMoment,
        design,
        body: html,
        newDesign: false,
      });

      // Clean up unused dynamic segments from the map
      const currentMap = dynamicSegmentIdMapRef.current;
      if (currentMap) {
        const cleanedMap = omitBy(currentMap, (segmentId) => {
          return !html?.includes(`{{#if ${segmentId}}}`);
        });
        onDynamicSegmentIdMapChange(Object.keys(cleanedMap).length > 0 ? cleanedMap : null);
      }
    },
    [emailMoment, onEmailMomentChange, onDynamicSegmentIdMapChange],
  );

  const {
    editor,
    onReady,
    save,
    dirty,
    reset,
    displayCondition: dc,
  } = useFollowUpUnlayerEditor({
    initialDesign: emailMoment?.design ?? null,
    onChange: handleDesignChange,
    displayCondition,
  });

  const unlayerMergeTags = mapUnlayerMergeTags(mergeTags, toTarget);
  const { loading: optionsLoading, data: options } = useUnlayerEditorOptions(unlayerMergeTags);

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  if (!emailMoment || optionsLoading) return <LinearProgress />;

  return (
    <Stack direction="column" spacing={2}>
      <EmailAddressSelect
        value={channel.channelData.email_sender || config.defaultEmailFrom}
        onChange={(email_sender) =>
          setChannelData({
            ...channel,
            channelData: {
              ...channel.channelData,
              email_sender,
            },
          })
        }
        disabled={disabled}
        restrictedEmailFrom={restrictedEmailFrom}
        fullWidth
        size="small"
        hasError={!!errors.email_sender}
        helperText={errors.email_sender}
      />
      <AutoCompleteSelector
        defaultValue={channel.channelData.email_subject || ""}
        onChange={(email_subject) =>
          setChannelData({
            ...channel,
            channelData: {
              ...channel.channelData,
              email_subject,
            },
          })
        }
        filterTags={filterTags}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            variant="outlined"
            label="Subject"
            placeholder="Enter your email subject"
            helperText={errors.email_subject ?? "Type {{ to insert a merge tag into your subject"}
            fullWidth
            size="small"
            error={!!errors.email_subject}
          />
        )}
      />
      <div style={{ flex: 1, display: "flex", minHeight: 750 }}>
        <UnlayerEmailEditor
          ref={editor}
          onReady={onReady}
          style={
            disabled
              ? { pointerEvents: "none", opacity: 0.6, filter: "grayscale(100%)" }
              : undefined
          }
          options={options}
        />
        <SimpleSaver ref={saverRef} save={save} dirty={dirty} reset={reset} />
        {dc.isOpen && (
          <DisplayConditionDialog
            open={dc.isOpen}
            onClose={dc.onClose}
            onConfirm={dc.onConfirm}
            selectedId={dc.selectedId}
            nestedBlocked={dc.nestedBlocked}
          />
        )}
      </div>
      {errors.design && (
        <FormHelperText error variant="filled">
          {errors.design}
        </FormHelperText>
      )}
    </Stack>
  );
};
